import { useEffect, useState, type FormEvent } from 'react';
import { ClientCreate, ClientUpdate, can, type Client, lockKeys } from '@torapon/shared';
import { Dialog, useToast } from '../components/ui';
import { api, HttpError } from '../lib/api';
import { useMe } from '../lib/me';
import { LockBanner, useEditLock } from '../lib/locks';

/** 荷主の登録（仕様書「荷主の欄」）。担当者は荷主アプリのときに足す */
export function ClientsPage() {
  const me = useMe();
  const toast = useToast();
  const [rows, setRows] = useState<Client[] | null>(null);
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState<Client | null>(null);
  const load = () => api.get<{ clients: Client[] }>('/v1/office/clients').then((r) => setRows(r.clients)).catch((e) => setErr(e instanceof HttpError ? e.message : '読み込めませんでした'));
  useEffect(() => { void load(); }, []);
  const canEdit = !!me && can(me.role, 'register');
  const add = async (e: FormEvent) => {
    e.preventDefault();
    const r = ClientCreate.safeParse({ name, showPhotos: false });
    if (!r.success) { setErr(r.error.issues[0]?.message ?? '入力を確かめてください'); return; }
    try { await api.post('/v1/office/clients', r.data); setName(''); setErr(''); toast('荷主を登録しました'); await load(); }
    catch (er) { setErr(er instanceof HttpError ? er.message : '登録できませんでした'); }
  };
  return (
    <div className="page" style={{ display: 'grid', gap: 12 }}>
      {err && <p className="err" role="alert">{err}</p>}
      <section className="panel">
        <table>
          <thead><tr><th>会社名</th><th>写真を見せる</th><th>状態</th>{canEdit && <th></th>}</tr></thead>
          <tbody>
            {rows === null ? <tr><td colSpan={4} className="muted">読み込み中…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={4} className="muted">まだ荷主がいません</td></tr>
              : rows.map((c) => (
                <tr key={c.id}><td><b>{c.name}</b></td><td>{c.showPhotos ? '見せる' : '見せない'}</td><td>{c.active ? '有効' : '無効'}</td>
                  {canEdit && <td><button className="btn sm" onClick={() => setEditing(c)}>直す</button></td>}</tr>
              ))}
          </tbody>
        </table>
        {canEdit && (
          <form onSubmit={add} style={{ padding: 16, display: 'grid', gap: 8, borderTop: '1px solid var(--line)' }}>
            <div className="field"><label htmlFor="cl-name">会社名</label><input id="cl-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <button className="btn pri" style={{ justifySelf: 'end' }}>登録する</button>
          </form>
        )}
      </section>
      {editing && <EditDialog client={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); toast('荷主を直しました'); void load(); }} />}
    </div>
  );
}

function EditDialog({ client, onClose, onDone }: { client: Client; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ name: client.name, showPhotos: client.showPhotos, active: client.active });
  const [err, setErr] = useState('');
  const lock = useEditLock(lockKeys.client(client.id));
  const save = async () => {
    const r = ClientUpdate.safeParse(f);
    if (!r.success) { setErr(r.error.issues[0]?.message ?? '入力を確かめてください'); return; }
    try { await api.patch(`/v1/office/clients/${client.id}`, r.data, { ifMatch: client.version }); onDone(); }
    catch (e) { setErr(e instanceof HttpError ? e.message : '保存できませんでした'); }
  };
  return (
    <Dialog title="荷主を直す" onClose={onClose}>
      <div className="field"><label htmlFor="cle-name">会社名</label><input id="cle-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
      <label><input type="checkbox" style={{ width: 'auto' }} checked={f.showPhotos} onChange={(e) => setF({ ...f, showPhotos: e.target.checked })} /> 荷主に写真を見せる</label>
      <label><input type="checkbox" style={{ width: 'auto' }} checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> 有効</label>
      <LockBanner lock={lock} what="この荷主" />
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={onClose}>やめる</button><button className="btn pri" disabled={!lock.mine} onClick={() => void save()}>保存する</button></div>
    </Dialog>
  );
}
